/* Search by image (snippets/image-search.liquid).
 *
 * The shopper's device only does two light things: shrink the chosen photo to ~512px
 * (canvas -> JPEG, ~50 KB) and show the results. The matching runs on the image-search
 * service (its URL is the theme setting "Image search endpoint"), which answers with
 * product ids, closest first; the cards themselves are the theme's own, fetched through
 * the section rendering API (sections/image-search-results.liquid).
 */
(() => {
  if (window.customElements.get('image-search')) return;

  const MAX_SIDE = 512;
  const RESULTS = 24;

  class ImageSearch extends HTMLElement {
    connectedCallback() {
      this.endpoint = (this.getAttribute('endpoint') || '').replace(/\/+$/, '');
      this.input = this.querySelector('[data-image-search-input]');
      this.modal = this.querySelector('x-modal');
      this.photo = this.querySelector('[data-image-search-photo]');
      this.statusEl = this.querySelector('[data-image-search-status]');
      this.body = this.querySelector('[data-image-search-body]');
      if (!this.endpoint || !this.input || !this.modal) return;

      // the modal lives inside the header search, which closes when another dialog opens:
      // park it on <body> so it outlives that
      this.headerSearch = this.closest('header-search');
      document.body.append(this.modal);

      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.querySelector('[data-image-search-open]').addEventListener('click', () => this.input.click(), options);
      this.modal.querySelector('[data-image-search-again]').addEventListener('click', () => this.input.click(), options);
      this.input.addEventListener('change', () => {
        const file = this.input.files && this.input.files[0];
        this.input.value = '';
        if (file) this.search(file);
      }, options);
      this.modal.addEventListener('click', (event) => {
        if (event.target.closest('[data-image-search-body] a')) this.track('image_search_click');
      }, options);
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    status(message) {
      this.statusEl.textContent = message;
    }

    // photo -> JPEG data URL, longest side MAX_SIDE. Keeps the upload tiny on any phone.
    async shrink(file) {
      const bitmap = await (window.createImageBitmap
        ? createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => null)
        : null) || await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('unreadable image'));
        img.src = URL.createObjectURL(file);
      });
      const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext('2d');
      context.fillStyle = '#fff';                       // transparent PNGs would turn black as JPEG
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.85);
    }

    async search(file) {
      const run = (this.run = (this.run || 0) + 1);      // a newer photo supersedes this one
      this.track('image_search_start');
      this.body.replaceChildren();
      this.photo.hidden = true;
      this.status('Looking at your photo…');
      this.headerSearch?.hide?.();                       // the search bar is a dialog too: results take over
      if (!this.modal.open) this.modal.show();

      try {
        if (!/^image\//.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) throw new Error('That file isn’t a picture. Choose a photo or a screenshot.');
        let image;
        try { image = await this.shrink(file); } catch (e) { throw new Error('We couldn’t open that picture. Try a JPG or PNG, or take a screenshot of it.'); }
        if (run !== this.run) return;
        this.photo.src = image;
        this.photo.hidden = false;
        this.status('Finding art that looks like this…');

        let response;
        try {
          response = await fetch(`${this.endpoint}/search`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ image, limit: RESULTS }) });
        } catch (e) {
          throw new Error('Image search is unavailable right now. Please try again in a moment.');   // network / service down
        }
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Image search is unavailable right now. Please try again.');
        if (run !== this.run) return;
        const ids = (data.matches || []).map((match) => match.id);
        if (!ids.length) throw new Error('No matches for that photo. Try one where the artwork fills more of the picture.');

        const query = ids.map((id) => `id:${id}`).join(' OR ');
        const html = await (await fetch(`${window.Shopify?.routes?.root || '/'}search?type=product&q=${encodeURIComponent(query)}&section_id=${encodeURIComponent(this.getAttribute('section-id'))}`)).text();
        if (run !== this.run) return;
        const results = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-image-search-results]');
        const count = results ? results.querySelectorAll('product-card, .product-card').length : 0;
        if (!count) throw new Error('No matches for that photo. Try one where the artwork fills more of the picture.');

        this.body.replaceChildren(...document.importNode(results, true).childNodes);
        this.status(`${count} designs that look like your photo, closest first.`);
        window.GooliArtGlobalDiscount?.refresh();        // card prices follow an applied sitewide code
        this.track('image_search_results', { image_search_count: count, image_search_ms: data.took_ms });
      } catch (error) {
        if (run !== this.run) return;
        this.status(error.message || 'Image search is unavailable right now. Please try again.');
        this.track('image_search_error');
      }
    }

    track(event, extra) {
      try { (window.dataLayer = window.dataLayer || []).push({ event, ...(extra || {}) }); } catch (e) {}
    }
  }

  window.customElements.define('image-search', ImageSearch);
})();
